import { useLingui } from '@lingui/react/macro';
import { useState } from 'react';
import { useFormContext, useWatch, type FieldValues, type Path, type PathValue } from 'react-hook-form';

import { Combobox, type ComboboxOption } from '@/shared/ui/combobox';
import type { FieldControlProps } from '@/shared/ui/field';

import { entityOptionCache } from './entity-cache';
import { useEntityById, useEntitySearch, type EntityOption, type EntitySource } from './entity-source';
import { Bound, type BoundFieldProps } from './fields';

export interface EntityPickerProps<T> {
  readonly source: EntitySource<T>;
  readonly value: string | null | undefined;
  /** Escolheu (ou limpou, com `null`). O item vem junto para quem grava campos extras (display, discriminador). */
  readonly onChange: (value: string | null, option: EntityOption | null, item: T | null) => void;
  /** Rótulo do valor atual que o formulário já tem (o `*Display` do fio). */
  readonly selectedLabel?: string | null | undefined;
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly control?: Partial<FieldControlProps>;
  readonly className?: string;
  readonly onBlur?: () => void;
}

/**
 * Seletor de entidade com busca remota (P06): espera 300 ms entre as teclas, mostra a lista anterior
 * enquanto a nova chega e acha o rótulo do valor salvo pela `byId` quando ele não veio na busca.
 */
export function EntityPicker<T>({ source, value, onChange, selectedLabel, placeholder, searchPlaceholder, control, className, onBlur }: EntityPickerProps<T>) {
  const { i18n, t } = useLingui();
  const [query, setQuery] = useState<string | null>(null);
  const results = useEntitySearch(source, query ?? '', query !== null);
  const items = results.data ?? [];
  const cached = value ? entityOptionCache.get(source, value) : undefined;
  const known = selectedLabel ?? cached?.label;
  const resolved = useEntityById(source, value, !known);
  const resolvedLabel = resolved.data === undefined ? undefined : source.toOption(resolved.data, i18n).label;

  const options: ComboboxOption[] = items.map((item) => {
    const option = source.toOption(item, i18n);
    return { value: option.value, label: option.label, ...(option.description ? { description: option.description } : {}) };
  });
  const label = known ?? resolvedLabel;

  return (
    <Combobox
      {...(control?.id ? { id: control.id } : {})}
      {...(control?.name ? { name: control.name } : {})}
      {...(control?.['aria-labelledby'] ? { 'aria-labelledby': control['aria-labelledby'] } : {})}
      {...(control?.['aria-describedby'] ? { 'aria-describedby': control['aria-describedby'] } : {})}
      {...(control?.['aria-invalid'] ? { 'aria-invalid': true } : {})}
      {...(control?.['aria-required'] ? { 'aria-required': true } : {})}
      disabled={control?.disabled === true}
      readOnly={control?.readOnly === true}
      options={options}
      value={value ?? null}
      loading={results.isFetching}
      onSearch={setQuery}
      {...(label ? { selectedLabel: label } : {})}
      placeholder={placeholder ?? t`Buscar`}
      searchPlaceholder={searchPlaceholder ?? t`Digite para buscar`}
      emptyMessage={results.isError ? t`Não foi possível buscar agora. Tente de novo.` : t`Nenhum resultado encontrado`}
      {...(className ? { className } : {})}
      onChange={(next, option) => {
        const index = next === null ? -1 : options.findIndex((candidate) => candidate.value === next);
        const item = index >= 0 ? (items[index] ?? null) : null;
        const entityOption = option ? { value: option.value, label: option.label } : null;
        if (next && entityOption) entityOptionCache.set(source, entityOption);
        onChange(next, entityOption, item);
        onBlur?.();
      }}
    />
  );
}

export interface EntityPickerFieldProps<V extends FieldValues, T> extends BoundFieldProps<V> {
  readonly source: EntitySource<T>;
  /** Onde gravar o rótulo escolhido, como o legado (`personIdDisplay`); também é lido como rótulo atual. */
  readonly displayName?: Path<V>;
  /** Campos extras a partir do item (ex.: `personDiscriminator`). Recebe `null` ao limpar. */
  readonly onSelect?: (item: T | null, setValue: (name: Path<V>, value: unknown) => void) => void;
  readonly placeholder?: string;
}

/** `EntityPicker` ligado ao react-hook-form: grava o id, o rótulo (opcional) e os extras. */
export function EntityPickerField<V extends FieldValues, T>(props: EntityPickerFieldProps<V, T>) {
  const { setValue, control: formControl } = useFormContext<V>();
  const watched: unknown = useWatch({ control: formControl, name: props.displayName ?? props.name });
  const display = props.displayName ? watched : undefined;
  const set = (name: Path<V>, value: unknown) => setValue(name, value as PathValue<V, Path<V>>, { shouldDirty: true });
  return (
    <Bound props={props}>
      {(control, { field }) => (
        <EntityPicker
          source={props.source}
          control={control}
          value={typeof field.value === 'string' ? field.value : null}
          selectedLabel={typeof display === 'string' ? display : null}
          onBlur={field.onBlur}
          {...(props.placeholder ? { placeholder: props.placeholder } : {})}
          onChange={(next, option, item) => {
            field.onChange(next);
            if (props.displayName) set(props.displayName, option?.label ?? null);
            props.onSelect?.(item, set);
          }}
        />
      )}
    </Bound>
  );
}
